/**
 * The stake arithmetic, with no Zod in it.
 *
 * **This file must never import a schema, and that is the whole reason it
 * exists.** It is reached from the browser as `@geni/contracts/money`, and
 * the package root pulls Zod in behind it — so a widget importing `assessStake`
 * from `@geni/contracts` ships a validation library on a betting page to
 * decide whether 20 is less than 100.
 *
 * This is the same mistake chunk 13 found in `view-model.ts`, which reached for
 * one string helper from `@geni/i18n` and dragged 372 kB of Zod and locale
 * files after it. Same fix, for the same reason: a subpath pointing at the pure
 * layer, rather than a second copy of the function that would drift from this
 * one the first time somebody adjusted a bound.
 *
 * The types below are structural on purpose. `PlayerAccount` is still inferred
 * from `PlayerAccountSchema` — the project's rule that runtime validation and
 * compile-time types come from one definition is not weakened here — and
 * `account.ts` carries a compile-time assertion that the inferred type still
 * satisfies these shapes. So the schema stays the source of truth, and this
 * file stays free of it.
 */

/**
 * Every defensible reading of a number a person typed, as a number.
 *
 * Grouping is not a detail that can be skipped. "1.000" is a thousand to a
 * German, Spanish or Turkish bettor and one to an English one, and both are
 * writing the same characters — so a parser that reads only one convention is
 * wrong for most of the languages this widget ships in. Before this existed the
 * server read every grouped amount as its first digit: "1,000" was 1, and
 * "1 500,50" was two numbers.
 *
 * Returns a LIST because some inputs are genuinely ambiguous and pretending
 * otherwise is how a bettor ends up staking 1 when they meant 1000. Exactly one
 * reading means the input decided it:
 *
 *   "1,500.50"  -> [1500.5]     both separators present; the last one is the
 *                               decimal and no convention disagrees
 *   "2.500,75"  -> [2500.75]    same rule, other way round
 *   "1.234.567" -> [1234567]    repeated, so it can only be grouping
 *   "50.5"      -> [50.5]       not three digits after, so not grouping
 *   "1,000"     -> [1000, 1]    genuinely ambiguous, and both are returned
 *
 * Callers decide what to do with an ambiguous answer. A field that must resolve
 * to one amount refuses it; a check asking "did they write this number at all"
 * accepts either.
 */
export function plausibleAmounts(raw: string): number[] {
  // Currency symbols and every kind of space, including the non-breaking ones
  // Intl.NumberFormat itself emits — so a value we formatted can be read back
  // after a bettor has edited it.
  const cleaned = raw.replace(/[\p{Zs}\s$£€₺₽₴¥₹₩₪¢]/gu, '');
  if (cleaned === '' || !/^[\d.,]+$/u.test(cleaned)) return [];

  const lastComma = cleaned.lastIndexOf(',');
  const lastDot = cleaned.lastIndexOf('.');

  const asNumber = (value: string): number[] => {
    if (!/^\d+(\.\d+)?$/u.test(value)) return [];
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? [parsed] : [];
  };

  if (lastComma >= 0 && lastDot >= 0) {
    const decimalAt = Math.max(lastComma, lastDot);
    return asNumber(
      cleaned.slice(0, decimalAt).replace(/[.,]/gu, '') + '.' + cleaned.slice(decimalAt + 1),
    );
  }

  if (lastComma < 0 && lastDot < 0) return asNumber(cleaned);

  const separator = lastComma >= 0 ? ',' : '.';
  const at = Math.max(lastComma, lastDot);
  const tail = cleaned.slice(at + 1);

  // Repeated, so it can only be grouping: "1.234.567".
  if (cleaned.indexOf(separator) !== at) return asNumber(cleaned.replace(/[.,]/gu, ''));

  // Exactly three digits after a single separator is the ambiguous case, and
  // the only one with two answers: grouping first, because a bettor who types a
  // separator at all usually means a thousand.
  if (tail.length === 3) {
    return [...asNumber(cleaned.replace(/[.,]/gu, '')), ...asNumber(cleaned.slice(0, at) + '.' + tail)];
  }

  return asNumber(cleaned.slice(0, at) + '.' + tail);
}

/** As much of a `PlayerAccount` as the arithmetic actually reads. */
export interface AccountLike {
  balance: number;
  minStake: number;
  maxStake: number;
  topUpAvailable: boolean;
}

/** Mirrors `StakeStatusSchema`. Kept in step by the assertion in account.ts. */
export type StakeStatusValue =
  | 'NEEDED'
  | 'BELOW_MINIMUM'
  | 'ABOVE_MAXIMUM'
  | 'EXCEEDS_BALANCE'
  | 'READY';

/** Mirrors `StakeSuggestionSchema`. */
export type StakeSuggestionValue =
  | { kind: 'STAKE'; amount: number }
  | { kind: 'TOP_UP' };

/** Two decimal places, without the drift that repeated float maths produces. */
export function roundMoney(amount: number): number {
  return Math.round((amount + Number.EPSILON) * 100) / 100;
}

/** What a winning bet returns, stake included. Ours to compute, never the model's. */
export function potentialPayout(stake: number, totalPrice: number): number {
  return roundMoney(stake * totalPrice);
}

/**
 * Round to a figure a person would actually type: 1, 2 or 5 times a power of
 * ten.
 *
 * Quick actions derived arithmetically from a balance produce numbers like
 * 23.47, which nobody chooses and which read as though the assistant has picked
 * the amount for them. Landing on round numbers keeps them legible as
 * shortcuts rather than as advice.
 */
export function niceStake(amount: number): number {
  if (!Number.isFinite(amount) || amount <= 0) return 0;

  const magnitude = 10 ** Math.floor(Math.log10(amount));
  const scaled = amount / magnitude;
  const step = scaled >= 5 ? 5 : scaled >= 2 ? 2 : 1;

  return roundMoney(step * magnitude);
}

export interface StakeAssessment {
  status: StakeStatusValue;
  suggestions: StakeSuggestionValue[];
}

/**
 * Where a named amount stands, and what to offer instead when it does not work.
 *
 * The one implementation, called by the API route and by the widget, because
 * the thing that must never happen is the two disagreeing about whether an
 * amount is affordable — a card offering to place a bet the server would refuse
 * is worse than no card at all.
 *
 * The order the checks run in is deliberate. A bettor who asks for more than
 * they hold is told about the balance, not about the operator's maximum, even
 * when both are true — the balance is the one they can do something about.
 */
export function assessStake(args: {
  amount: number | null;
  account: AccountLike | null;
  /** Whether a deposit prompt is permitted here at all. */
  allowTopUp?: boolean;
}): StakeAssessment {
  const { amount, account } = args;
  const allowTopUp = (args.allowTopUp ?? false) && (account?.topUpAvailable ?? false);

  if (amount === null || !Number.isFinite(amount) || amount <= 0) {
    return { status: 'NEEDED', suggestions: openingSuggestions(account) };
  }

  // No account source configured: nothing to check against, so nothing to
  // warn about. Deliberately not treated as "refuse everything" — that would
  // take a working feature off any operator who has not integrated a wallet.
  if (account === null) return { status: 'READY', suggestions: [] };

  if (amount > account.balance) {
    return {
      status: 'EXCEEDS_BALANCE',
      suggestions: [
        ...affordableAlternatives(account),
        ...(allowTopUp ? [{ kind: 'TOP_UP' as const }] : []),
      ],
    };
  }

  if (amount < account.minStake) {
    return {
      status: 'BELOW_MINIMUM',
      suggestions: [{ kind: 'STAKE', amount: roundMoney(account.minStake) }],
    };
  }

  if (amount > account.maxStake) {
    return {
      status: 'ABOVE_MAXIMUM',
      suggestions: [{ kind: 'STAKE', amount: roundMoney(account.maxStake) }],
    };
  }

  return { status: 'READY', suggestions: [] };
}

/** The ceiling on any suggested amount: never more than is there to bet with. */
function affordableCeiling(account: AccountLike): number {
  return Math.min(account.balance, account.maxStake);
}

/**
 * What to offer somebody who has not named an amount yet.
 *
 * Three modest fractions of what they hold rather than the whole balance. The
 * shortcut that stakes everything is the one nobody should reach by accident,
 * and offering it first would make the default the biggest one on screen.
 */
function openingSuggestions(account: AccountLike | null): StakeSuggestionValue[] {
  if (account === null) return [];

  const ceiling = affordableCeiling(account);
  return distinctStakes([0.05, 0.1, 0.25].map((share) => niceStake(ceiling * share)), account);
}

/**
 * What to offer somebody who asked for more than they hold.
 *
 * The balance itself, because that is the answer to "what can I actually put
 * on", and half of it, because the first is not a suggestion anybody should be
 * nudged towards on its own.
 */
function affordableAlternatives(account: AccountLike): StakeSuggestionValue[] {
  const ceiling = affordableCeiling(account);
  return distinctStakes([roundMoney(ceiling), niceStake(ceiling / 2)], account);
}

/** Inside the operator's bounds, deduplicated, ascending. */
function distinctStakes(amounts: number[], account: AccountLike): StakeSuggestionValue[] {
  const ceiling = affordableCeiling(account);
  const seen = new Set<number>();

  return amounts
    .map((amount) => roundMoney(amount))
    // Nothing at or below zero, whatever the account says. A minimum of 0 and a
    // maximum of 0 is a misconfiguration rather than a wallet, but it used to
    // pass every bound here and put a "Place 0.00" button on screen — an offer
    // to stake nothing, on the one panel where every button moves money. The
    // configuration that caused it is fixed; this is the line that means no
    // configuration can cause it again.
    .filter((amount) => amount > 0)
    .filter((amount) => amount >= account.minStake && amount <= ceiling)
    .filter((amount) => (seen.has(amount) ? false : (seen.add(amount), true)))
    .sort((left, right) => left - right)
    .map((amount) => ({ kind: 'STAKE' as const, amount }));
}
